import { morph } from '@theme/morph';

/**
 * @typedef {Object} SectionRequestContext
 * @property {string} sectionId - Normalized renderer section ID.
 * @property {string} stateURL - Canonical cache/request state without a fragment or section_id.
 * @property {string} fetchURL - Section Rendering API URL with exactly one renderer section_id.
 * @property {string} cacheKey - Cache identity containing both the renderer section and state URL.
 *
 * @typedef {Object} SectionHTMLResult
 * @property {string} html
 * @property {'cache' | 'network'} source
 * @property {SectionRequestContext} request
 * @property {SectionRequestAuthority} authority
 * @property {boolean} reusable
 *
 * @typedef {Object} SectionRequestAuthority
 * @property {string} cacheKey
 * @property {number} generation
 * @property {symbol} token
 *
 * @typedef {Object} PreparedSectionRender
 * @property {string} sectionId
 * @property {string} requestURL
 * @property {'cache' | 'network'} source
 * @property {AbortSignal} signal
 * @property {Promise<SectionHTMLResult>} ready
 * @property {() => void} abort
 * @property {() => Promise<SectionRenderOutcome>} commit
 *
 * @typedef {Object} SectionRenderOutcome
 * @property {'success' | 'superseded'} status
 * @property {string} html
 * @property {'cache' | 'network'} source
 * @property {string} requestURL
 */

/**
 * A class to re-render sections using the Section Rendering API.
 */
class SectionRenderer {
  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {Map<string, SectionRequestAuthority>} */
  #cacheAuthorities = new Map();

  /** @type {Map<string, AbortController>} */
  #abortControllersBySectionId = new Map();

  /** @type {Map<string, { authority: SectionRequestAuthority, promise: Promise<string> }>} */
  #pendingPromises = new Map();

  /** @type {Map<string, SectionRequestAuthority>} */
  #requestAuthorities = new Map();

  /** @type {Map<string, number>} */
  #requestGenerations = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Creates a render whose HTML can be resolved before its guarded morph is committed.
   * Creating a newer render synchronously invalidates the older morph for the same section.
   *
   * @param {string} sectionId
   * @param {Object} [options]
   * @param {boolean} [options.cache]
   * @param {boolean} [options.fresh]
   * @param {URL} [options.url]
   * @returns {PreparedSectionRender}
   */
  createSectionRender(sectionId, options) {
    const { cache = !Shopify.designMode, fresh = false } = options ?? {};
    const { url = new URL(window.location.href) } = options ?? {};
    const request = buildSectionRequestContext(sectionId, url);

    this.#abortPendingMorph(request.sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(request.sectionId, abortController);

    const releaseController = () => {
      if (this.#abortControllersBySectionId.get(request.sectionId) === abortController) {
        this.#abortControllersBySectionId.delete(request.sectionId);
      }
    };

    const isCurrent = () =>
      !abortController.signal.aborted &&
      this.#abortControllersBySectionId.get(request.sectionId) === abortController;

    const source =
      !fresh && cache && this.#cache.has(request.cacheKey)
        ? /** @type {'cache'} */ ('cache')
        : /** @type {'network'} */ ('network');

    /** @type {SectionRequestAuthority | undefined} */
    let ownedRequestAuthority;
    const revokeOwnedRequestAuthority = () => {
      if (ownedRequestAuthority) this.#revokeRequestAuthority(ownedRequestAuthority);
    };
    abortController.signal.addEventListener('abort', revokeOwnedRequestAuthority, { once: true });

    const ready = (async () => {
      let resolved = false;

      try {
        const result = await this.#getSectionHTMLResult(request, cache, fresh, (authority) => {
          ownedRequestAuthority = authority;
        });
        resolved = true;
        return result;
      } finally {
        if (!resolved) releaseController();
      }
    })();

    return {
      sectionId: request.sectionId,
      requestURL: request.stateURL,
      source,
      signal: abortController.signal,
      ready,
      abort: () => {
        abortController.abort();
        releaseController();
      },
      commit: async () => {
        try {
          const result = await ready;

          if (!isCurrent() || !this.#isRequestAuthorityCurrent(result.authority)) {
            return {
              status: /** @type {'superseded'} */ ('superseded'),
              html: result.html,
              source: result.source,
              requestURL: request.stateURL,
            };
          }

          try {
            await morphSection(request.sectionId, result.html);
          } catch (error) {
            this.#deleteCacheIfOwned(result.authority);
            throw error;
          }

          if (!isCurrent() || !this.#isRequestAuthorityCurrent(result.authority)) {
            return {
              status: /** @type {'superseded'} */ ('superseded'),
              html: result.html,
              source: result.source,
              requestURL: request.stateURL,
            };
          }

          return {
            status: /** @type {'success'} */ ('success'),
            html: result.html,
            source: result.source,
            requestURL: request.stateURL,
          };
        } finally {
          releaseController();
        }
      },
    };
  }

  /**
   * Renders a section immediately through the same prepare/commit contract.
   *
   * @param {string} sectionId
   * @param {Object} [options]
   * @param {boolean} [options.cache]
   * @param {URL} [options.url]
   * @returns {Promise<string>}
   */
  async renderSection(sectionId, options) {
    const render = this.createSectionRender(sectionId, options);

    try {
      await render.ready;
      const outcome = await render.commit();
      return outcome.html;
    } catch (error) {
      render.abort();
      throw error;
    }
  }

  /**
   * Aborts an existing morph for a section.
   *
   * @param {string} sectionId
   */
  #abortPendingMorph(sectionId) {
    this.#abortControllersBySectionId.get(normalizeSectionId(sectionId))?.abort();
  }

  /**
   * Gets section HTML and reports the actual cache/network source.
   *
   * @param {string} sectionId
   * @param {boolean} useCache
   * @param {URL} url
   * @param {{ fresh?: boolean }} [options]
   * @returns {Promise<SectionHTMLResult>}
   */
  async getSectionHTMLWithSource(
    sectionId,
    useCache = true,
    url = new URL(window.location.href),
    options
  ) {
    return this.#getSectionHTMLResult(
      buildSectionRequestContext(sectionId, url),
      useCache,
      options?.fresh === true
    );
  }

  /**
   * Gets the HTML for a section.
   *
   * @param {string} sectionId
   * @param {boolean} useCache
   * @param {URL} url
   * @param {{ fresh?: boolean }} [options]
   * @returns {Promise<string>}
   */
  async getSectionHTML(
    sectionId,
    useCache = true,
    url = new URL(window.location.href),
    options
  ) {
    const result = await this.getSectionHTMLWithSource(sectionId, useCache, url, options);
    return result.html;
  }

  /**
   * @param {SectionRequestContext} request
   * @param {boolean} useCache
   * @param {boolean} fresh
   * @param {(authority: SectionRequestAuthority) => void} [onAuthority]
   * @returns {Promise<SectionHTMLResult>}
   */
  async #getSectionHTMLResult(request, useCache, fresh = false, onAuthority) {
    const pendingRecord = this.#pendingPromises.get(request.cacheKey);
    if (!fresh && pendingRecord) {
      const html = await pendingRecord.promise;
      return {
        html,
        source: 'network',
        request,
        authority: pendingRecord.authority,
        reusable: this.#isRequestAuthorityCurrent(pendingRecord.authority),
      };
    }

    if (!fresh && useCache && this.#cache.has(request.cacheKey)) {
      const authority = this.#cacheAuthorities.get(request.cacheKey);
      if (authority) {
        return {
          html: this.#cache.get(request.cacheKey) ?? '',
          source: 'cache',
          request,
          authority,
          reusable: this.#isRequestAuthorityCurrent(authority),
        };
      }
    }

    if (fresh) {
      this.#cache.delete(request.cacheKey);
      this.#cacheAuthorities.delete(request.cacheKey);
    }

    const authority = this.#createRequestAuthority(request.cacheKey);
    onAuthority?.(authority);

    const requestPromise = fetch(request.fetchURL).then((response) => {
      if (response.ok === false) {
        throw new Error(`Section rendering request failed with status ${response.status}`);
      }

      return response.text();
    });
    const currentRecord = { authority, promise: requestPromise };
    this.#pendingPromises.set(request.cacheKey, currentRecord);

    try {
      const html = await requestPromise;
      const reusable = this.#isRequestAuthorityCurrent(authority);
      if (reusable) {
        this.#cache.set(request.cacheKey, html);
        this.#cacheAuthorities.set(request.cacheKey, authority);
      }

      return {
        html,
        source: 'network',
        request,
        authority,
        reusable,
      };
    } finally {
      if (this.#pendingPromises.get(request.cacheKey) === currentRecord) {
        this.#pendingPromises.delete(request.cacheKey);
      }
    }
  }

  /**
   * Creates the sole cache-publication authority for one canonical cache key.
   * A later request replaces this authority without cancelling existing consumers.
   *
   * @param {string} cacheKey
   * @returns {SectionRequestAuthority}
   */
  #createRequestAuthority(cacheKey) {
    const generation = (this.#requestGenerations.get(cacheKey) ?? 0) + 1;
    this.#requestGenerations.set(cacheKey, generation);

    const authority = Object.freeze({
      cacheKey,
      generation,
      token: Symbol(`${cacheKey}:${generation}`),
    });
    this.#requestAuthorities.set(cacheKey, authority);
    return authority;
  }

  /**
   * @param {SectionRequestAuthority} authority
   * @returns {boolean}
   */
  #isRequestAuthorityCurrent(authority) {
    return this.#requestAuthorities.get(authority.cacheKey) === authority;
  }

  /**
   * Revokes only the exact request authority owned by an aborted prepared render.
   * Existing consumers retain their promise, while later callers cannot adopt it.
   *
   * @param {SectionRequestAuthority} authority
   */
  #revokeRequestAuthority(authority) {
    if (!this.#isRequestAuthorityCurrent(authority)) return;

    this.#requestAuthorities.delete(authority.cacheKey);
    const pendingRecord = this.#pendingPromises.get(authority.cacheKey);
    if (pendingRecord?.authority === authority) {
      this.#pendingPromises.delete(authority.cacheKey);
    }
    this.#deleteCacheIfOwned(authority);
  }

  /**
   * @param {SectionRequestAuthority} authority
   */
  #deleteCacheIfOwned(authority) {
    if (this.#cacheAuthorities.get(authority.cacheKey) !== authority) return;
    this.#cache.delete(authority.cacheKey);
    this.#cacheAuthorities.delete(authority.cacheKey);
  }

  /**
   * Caches the initial page sections using the same canonical request context.
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      if (containsShadowRoot(section)) continue;

      const request = buildSectionRequestContext(section.id);
      if (
        !this.#cache.has(request.cacheKey) &&
        !this.#pendingPromises.has(request.cacheKey)
      ) {
        const authority = this.#createRequestAuthority(request.cacheKey);
        this.#cache.set(request.cacheKey, section.outerHTML);
        this.#cacheAuthorities.set(request.cacheKey, authority);
      }
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds the canonical request/cache contract without mutating the supplied URL.
 *
 * @param {string} sectionId
 * @param {URL | string} url
 * @returns {SectionRequestContext}
 */
export function buildSectionRequestContext(
  sectionId,
  url = new URL(window.location.href)
) {
  const normalizedSectionId = normalizeSectionId(sectionId);
  const stateURL = new URL(url.toString(), window.location.origin);
  stateURL.hash = '';
  stateURL.searchParams.delete('section_id');
  stateURL.searchParams.sort();

  const fetchURL = new URL(stateURL.toString());
  fetchURL.searchParams.append('section_id', normalizedSectionId);
  fetchURL.searchParams.sort();

  return {
    sectionId: normalizedSectionId,
    stateURL: stateURL.toString(),
    fetchURL: fetchURL.toString(),
    cacheKey: `${normalizedSectionId}:${stateURL.toString()}`,
  };
}

/**
 * Builds a section selector.
 *
 * @param {string} sectionId
 * @returns {string}
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${normalizeSectionId(sectionId)}`;
}

/**
 * Normalizes a section ID.
 *
 * @param {string} sectionId
 * @returns {string}
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root.
 *
 * @param {Element} element
 * @returns {boolean}
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * Morphs the existing section element with the new section contents.
 *
 * @param {string} sectionId
 * @param {string} html
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement);
}

export const sectionRenderer = new SectionRenderer();
